import {
    Box,
    Button,
    Flex,
    HStack,
    Icon,
    IconButton,
    Input,
    InputGroup,
    InputRightElement,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Tag,
    TagLabel,
    Text,
    useDisclosure,
    useToast,
    useColorModeValue,
    VStack,
    Drawer,
    DrawerOverlay,
    DrawerContent,
} from '@chakra-ui/react';
import {
    MdAdd,
    MdChat,
    MdDelete,
    MdEdit,
    MdMoreVert,
} from 'react-icons/md';
import { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { aiApi } from '@/services/api';
import { BanditIcon } from '@/components/Common/BanditIcon';
import {
    useChatHistory,
    useChatSessions,
    useCreateChatSession,
    useRenameChatSession,
    useDeleteChatSession
} from '@/services/hooks';
import { RenameDialog } from './RenameDialog';
import { ConfirmDialog } from '@/components/Common/ConfirmDialog';

interface ChatInterfaceProps {
    importedItemId: number;
    onOpenPreview?: (page?: number) => void;
    height?: string | number;
}

export function ChatInterface({ importedItemId, onOpenPreview, height = "100%" }: ChatInterfaceProps) {
    const [messages, setMessages] = useState<{ role: 'user' | 'ai'; content: string }[]>([]);
    const [chatInput, setChatInput] = useState('');
    const [isStreaming, setIsStreaming] = useState(false);
    const [currentSessionId, setCurrentSessionId] = useState<number | null>(null);

    const [sessionToRename, setSessionToRename] = useState<{ id: number; title: string } | null>(null);
    const { isOpen: isRenameOpen, onOpen: onRenameOpen, onClose: onRenameClose } = useDisclosure();
    const { isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose } = useDisclosure();
    const [confirmConfig, setConfirmConfig] = useState<{
        title: string;
        message: string;
        action: () => Promise<void>;
        confirmText?: string;
        confirmColor?: string;
    } | null>(null);

    const toast = useToast();
    const chatScrollRef = useRef<HTMLDivElement>(null);

    // Theme-aware colors
    const chatBg = useColorModeValue('white', 'gray.900');
    const sidebarBg = useColorModeValue('gray.50', 'rgba(0,0,0,0.2)');
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const userBubbleBg = useColorModeValue('brand.600', 'brand.500');
    const aiBubbleBg = useColorModeValue('gray.50', 'gray.800');
    const aiBubbleColor = useColorModeValue('gray.900', 'white');
    const aiBorderColor = useColorModeValue('gray.200', 'gray.700');
    const inputBg = useColorModeValue('white', 'gray.800');
    const inputAreaBg = useColorModeValue('gray.50', 'whiteAlpha.50');

    // Queries & Mutations
    const { data: sessions = [] } = useChatSessions({ imported_item_id: importedItemId });
    const { data: historyData } = useChatHistory({
        session_id: currentSessionId || undefined,
        imported_item_id: !currentSessionId ? importedItemId : undefined,
    });

    const createSessionMutation = useCreateChatSession();
    const renameSessionMutation = useRenameChatSession();
    const deleteSessionMutation = useDeleteChatSession();

    // Auto-scroll
    useEffect(() => {
        if (chatScrollRef.current) {
            chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
        }
    }, [messages, isStreaming]);

    // Pick first session
    useEffect(() => {
        if (sessions?.length && !currentSessionId) {
            setCurrentSessionId(sessions[0].id);
        }
    }, [sessions, currentSessionId]);

    // Load history
    useEffect(() => {
        if (historyData?.messages) {
            setMessages(historyData.messages.map((msg: any) => ({
                role: msg.role,
                content: msg.content
            })));
        } else if (!currentSessionId) {
            setMessages([]);
        }
    }, [historyData, currentSessionId]);

    const handleSendMessage = async (payloadOverride?: string) => {
        const textToSend = payloadOverride || chatInput;
        if (!textToSend.trim() || isStreaming) return;

        setChatInput('');
        setMessages(prev => [...prev, { role: 'user', content: textToSend }]);
        setIsStreaming(true);

        try {
            let sessionId = currentSessionId;
            if (!sessionId) {
                const newSession = await createSessionMutation.mutateAsync({
                    imported_item_id: importedItemId,
                    title: textToSend.substring(0, 30),
                });
                sessionId = newSession.id;
                setCurrentSessionId(sessionId);
            }
            if (!sessionId) throw new Error("Failed to create session");

            setMessages(prev => [...prev, { role: 'ai', content: '' }]);

            const fullTextRef = { current: '' };
            const displayedTextRef = { current: '' };

            const streamInterval = setInterval(() => {
                if (displayedTextRef.current.length < fullTextRef.current.length) {
                    const delta = fullTextRef.current.length - displayedTextRef.current.length;
                    // If we are far behind, jump faster. Otherwise, add a few chars for "flow".
                    const charsToAppend = delta > 100 ? Math.ceil(delta / 4) : Math.ceil(Math.random() * 3) + 2;
                    displayedTextRef.current = fullTextRef.current.substring(0, displayedTextRef.current.length + charsToAppend);

                    setMessages(prev => {
                        const updated = [...prev];
                        const last = updated[updated.length - 1];
                        if (last?.role === 'ai') {
                            last.content = displayedTextRef.current;
                        }
                        return [...updated];
                    });
                }
            }, 40);

            try {
                await aiApi.chatStream(
                    textToSend,
                    importedItemId,
                    sessionId,
                    (chunk) => {
                        fullTextRef.current += chunk;
                    },
                );

                // Final catch-up
                let attempts = 0;
                while (displayedTextRef.current.length < fullTextRef.current.length && attempts < 50) {
                    await new Promise(r => setTimeout(r, 40));
                    attempts++;
                }
            } finally {
                clearInterval(streamInterval);
                // Ensure 100% match at the end
                setMessages(prev => {
                    const updated = [...prev];
                    const last = updated[updated.length - 1];
                    if (last?.role === 'ai') last.content = fullTextRef.current;
                    return [...updated];
                });
                setIsStreaming(false);
            }

        } catch {
            toast({ title: 'Error sending message', status: 'error', duration: 3000 });
            setMessages(prev => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last?.role === 'ai') last.content = 'Sorry, I encountered an error. Please try again.';
                return updated;
            });
        } finally {
            setIsStreaming(false);
        }
    };

    const handleCreateSession = async () => {
        try {
            const s = await createSessionMutation.mutateAsync({ imported_item_id: importedItemId, title: 'New Chat' });
            setCurrentSessionId(s.id);
            setMessages([]);
            toast({ title: 'New chat thread created', status: 'success', duration: 2000 });
        } catch {
            toast({ title: 'Failed to create chat', status: 'error' });
        }
    };

    const handleRenameInit = (id: number, currentTitle: string) => {
        setSessionToRename({ id, title: currentTitle });
        onRenameOpen();
    };

    const handleRenameConfirm = async (newTitle: string) => {
        if (!sessionToRename) return;
        try {
            await renameSessionMutation.mutateAsync({ sessionId: sessionToRename.id, title: newTitle });
            onRenameClose();
            toast({ title: 'Chat renamed', status: 'success' });
        } catch {
            toast({ title: 'Failed to rename', status: 'error' });
        }
    };

    const handleDeleteInit = (id: number) => {
        setConfirmConfig({
            title: 'Delete Chat',
            message: 'Are you sure? This cannot be undone.',
            confirmText: 'Delete',
            confirmColor: 'red',
            action: async () => {
                await deleteSessionMutation.mutateAsync(id);
                if (currentSessionId === id) { setCurrentSessionId(null); setMessages([]); }
                onConfirmClose();
                toast({ title: 'Chat deleted', status: 'success' });
            },
        });
        onConfirmOpen();
    };

    const renderMessageContent = (content: string) => {
        const processed = content.replace(
            /\[Page (\d+)\]/g,
            (_match, num) => `[📄 Page ${num}](#citation-page-${num})`
        );
        return (
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                    p: ({ children }) => <Text fontSize="sm" mb={2} color="inherit">{children}</Text>,
                    li: ({ children }) => <Box as="li" fontSize="sm" ml={4} mb={1} color="inherit">{children}</Box>,
                    ul: ({ children }) => <Box as="ul" mb={2} color="inherit">{children}</Box>,
                    ol: ({ children }) => <Box as="ol" mb={2} color="inherit">{children}</Box>,
                    table: ({ children }) => (
                        <Box overflowX="auto" my={3} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                            <Box as="table" w="full" fontSize="xs" color="inherit" sx={{ 'th, td': { p: 2, borderBottom: '1px solid', borderColor } }}>
                                {children}
                            </Box>
                        </Box>
                    ),
                    a: ({ href, children }) => {
                        const match = href?.match(/^#citation-page-(\d+)$/);
                        if (match) {
                            return (
                                <Button
                                    size="xs"
                                    variant="solid"
                                    colorScheme="gray"
                                    fontWeight="bold"
                                    onClick={(e) => { e.stopPropagation(); onOpenPreview?.(parseInt(match[1], 10)); }}
                                >
                                    {children}
                                </Button>
                            );
                        }
                        return <Text as="a" href={href} color="brand.500" textDecoration="underline" target="_blank">{children}</Text>;
                    },
                }}
            >
                {processed}
            </ReactMarkdown>
        );
    };

    const smartChips = [
        { label: 'Check Compliance', query: 'List all compliance requirements for this bid.' },
        { label: 'Key Deadlines', query: 'What are the critical dates and deadlines mentioned?' },
        { label: 'Margin Killers', query: 'Are there any high-risk clauses or unusual requirements in this RFP?' },
        { label: 'Bond Requirements', query: 'What are the bonding and insurance requirements?' }
    ];

    const { isOpen: isSessionsOpen, onOpen: onSessionsOpen, onClose: onSessionsClose } = useDisclosure();

    const SessionList = () => (
        <VStack spacing={2} align="stretch" py={2}>
            <Button
                size="sm"
                leftIcon={<MdAdd />}
                colorScheme="brand"
                variant="ghost"
                onClick={() => { handleCreateSession(); onSessionsClose(); }}
                borderRadius="lg"
                mb={2}
                justifyContent="flex-start"
            >
                New Thread
            </Button>
            {sessions.map((session: any) => (
                <HStack
                    key={session.id}
                    p={2}
                    borderRadius="md"
                    cursor="pointer"
                    bg={currentSessionId === session.id ? 'brand.100' : 'transparent'}
                    _dark={{ bg: currentSessionId === session.id ? 'brand.900' : 'transparent' }}
                    _hover={{ bg: currentSessionId === session.id ? 'brand.200' : 'blackAlpha.100', _dark: { bg: 'whiteAlpha.100' } }}
                    onClick={() => { setCurrentSessionId(session.id); onSessionsClose(); }}
                    role="group"
                >
                    <Icon as={MdChat} color={currentSessionId === session.id ? 'brand.500' : 'gray.400'} boxSize={3} />
                    <Text fontSize="xs" fontWeight={currentSessionId === session.id ? 'bold' : 'normal'} noOfLines={1} flex={1}>
                        {session.title}
                    </Text>
                    <Menu isLazy>
                        <MenuButton
                            as={IconButton}
                            size="xs"
                            icon={<MdMoreVert />}
                            variant="ghost"
                            opacity={0}
                            _groupHover={{ opacity: 1 }}
                            minW="auto"
                            h="20px"
                        />
                        <MenuList zIndex={100} minW="140px">
                            <MenuItem icon={<MdEdit />} fontSize="xs" onClick={(e) => { e.stopPropagation(); handleRenameInit(session.id, session.title); }}>Rename</MenuItem>
                            <MenuItem icon={<MdDelete />} fontSize="xs" color="red.500" onClick={(e) => { e.stopPropagation(); handleDeleteInit(session.id); }}>Delete</MenuItem>
                        </MenuList>
                    </Menu>
                </HStack>
            ))}
        </VStack>
    );

    return (
        <Flex h={height} overflow="hidden" direction="column" borderRadius="xl" borderWidth="1px" borderColor={borderColor}>
            {/* Mobile Header with Session Toggle */}
            <Flex
                display={{ base: 'flex', md: 'none' }}
                p={2}
                bg={sidebarBg}
                borderBottomWidth="1px"
                borderColor={borderColor}
                justify="space-between"
                align="center"
            >
                <HStack>
                    <IconButton
                        size="sm"
                        icon={<MdChat />}
                        onClick={onSessionsOpen}
                        aria-label="Toggle threads"
                        variant="ghost"
                        colorScheme="brand"
                    />
                    <Text fontSize="xs" fontWeight="bold">
                        {sessions.find((s: any) => s.id === currentSessionId)?.title || 'Chat'}
                    </Text>
                </HStack>
                <Button size="xs" colorScheme="brand" variant="outline" onClick={handleCreateSession} leftIcon={<MdAdd />}>
                    New
                </Button>
            </Flex>

            <Flex flex={1} overflow="hidden" direction="row">
                {/* Desktop Session Sidebar */}
                <Box
                    w="200px"
                    bg={sidebarBg}
                    borderRightWidth="1px"
                    borderColor={borderColor}
                    p={3}
                    display={{ base: 'none', md: 'block' }}
                    overflowY="auto"
                >
                    <SessionList />
                </Box>

                {/* Mobile Sessions Drawer */}
                <Drawer
                    isOpen={isSessionsOpen}
                    placement="left"
                    onClose={onSessionsClose}
                >
                    <DrawerOverlay />
                    <DrawerContent bg={chatBg} p={4}>
                        <Text fontWeight="bold" mb={4}>Chat Threads</Text>
                        <SessionList />
                    </DrawerContent>
                </Drawer>

                {/* Chat Area */}
                <Flex flex={1} direction="column" bg={chatBg}>
                    <VStack ref={chatScrollRef} flex="1" align="stretch" spacing={4} overflowY="auto" px={6} py={6}>
                        {messages.length === 0 ? (
                            <VStack flex="1" justify="center" align="center" spacing={4}>
                                <BanditIcon boxSize={20} />
                                <Text fontWeight="bold" fontSize="lg" color="gray.600" _dark={{ color: 'gray.400' }}>AI Assistant</Text>
                                <Text textAlign="center" fontSize="sm" color="gray.500" maxW="300px">
                                    Ask anything about the scope, compliance, or risks in this RFP.
                                </Text>
                                {/* Suggestions */}
                                <HStack spacing={2} mt={4} wrap="wrap" justify="center">
                                    {smartChips.slice(0, 2).map((chip, idx) => (
                                        <Tag key={idx} size="md" variant="subtle" colorScheme="gray" borderRadius="full" cursor="pointer" onClick={() => handleSendMessage(chip.query)} _hover={{ bg: 'gray.100' }}>
                                            <TagLabel fontSize="xs">{chip.label}</TagLabel>
                                        </Tag>
                                    ))}
                                </HStack>
                            </VStack>
                        ) : (
                            messages.map((msg, idx) => (
                                <Box
                                    key={idx}
                                    alignSelf={msg.role === 'user' ? 'flex-end' : 'flex-start'}
                                    bg={msg.role === 'user' ? userBubbleBg : aiBubbleBg}
                                    color={msg.role === 'user' ? 'white' : aiBubbleColor}
                                    px={4}
                                    py={3}
                                    borderRadius="xl"
                                    borderTopRightRadius={msg.role === 'user' ? '2px' : 'xl'}
                                    borderTopLeftRadius={msg.role === 'user' ? 'xl' : '2px'}
                                    boxShadow="sm"
                                    maxW="85%"
                                    border="1px"
                                    borderColor={msg.role === 'user' ? 'transparent' : aiBorderColor}
                                >
                                    {msg.role === 'ai' ? (
                                        <Box className="markdown-body" fontSize="sm">{renderMessageContent(msg.content)}</Box>
                                    ) : (
                                        <Text fontSize="sm" color="white" fontWeight="medium">{msg.content}</Text>
                                    )}
                                </Box>
                            ))
                        )}
                        {isStreaming && messages[messages.length - 1]?.content === '' && (
                            <Box alignSelf="flex-start" bg={aiBubbleBg} px={4} py={3} borderRadius="xl" borderTopLeftRadius="2px" border="1px" borderColor={aiBorderColor}>
                                <HStack spacing={1}>
                                    <Box w={2} h={2} bg="gray.400" borderRadius="full" className="bounce-1" />
                                    <Box w={2} h={2} bg="gray.400" borderRadius="full" className="bounce-2" />
                                    <Box w={2} h={2} bg="gray.400" borderRadius="full" className="bounce-3" />
                                </HStack>
                            </Box>
                        )}
                    </VStack>

                    {/* Input Area */}
                    <Box p={4} borderTopWidth="1px" bg={inputAreaBg} borderColor={borderColor}>
                        <HStack spacing={2} mb={3} wrap="wrap">
                            {smartChips.map((chip, idx) => (
                                <Tag key={idx} size="md" variant="subtle" colorScheme="brand" borderRadius="full" cursor="pointer" onClick={() => handleSendMessage(chip.query)} _hover={{ bg: 'brand.100' }}>
                                    <TagLabel fontSize="xs">{chip.label}</TagLabel>
                                </Tag>
                            ))}
                        </HStack>
                        <InputGroup size="md">
                            <Input
                                pr="4.5rem"
                                placeholder="Ask your AI agent..."
                                value={chatInput}
                                onChange={(e) => setChatInput(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
                                bg={inputBg}
                                _dark={{ bg: 'gray.800' }}
                                borderRadius="lg"
                                fontSize="sm"
                                _focus={{ borderColor: 'brand.500', boxShadow: 'none' }}
                            />
                            <InputRightElement width="4.5rem">
                                <Button
                                    h="1.75rem"
                                    size="sm"
                                    colorScheme="brand"
                                    isDisabled={!chatInput || isStreaming}
                                    isLoading={isStreaming}
                                    onClick={() => handleSendMessage()}
                                    borderRadius="md"
                                >
                                    Send
                                </Button>
                            </InputRightElement>
                        </InputGroup>
                    </Box>
                </Flex>

                <RenameDialog
                    isOpen={isRenameOpen}
                    onClose={onRenameClose}
                    initialTitle={sessionToRename?.title || ''}
                    onRename={handleRenameConfirm}
                />

                {confirmConfig && (
                    <ConfirmDialog
                        isOpen={isConfirmOpen}
                        onClose={onConfirmClose}
                        title={confirmConfig.title}
                        message={confirmConfig.message}
                        confirmText={confirmConfig.confirmText}
                        confirmColor={confirmConfig.confirmColor}
                        onConfirm={confirmConfig.action}
                        isLoading={deleteSessionMutation.isPending}
                    />
                )}
                <style>{`
                .bounce-1 { animation: bounce 0.6s infinite ease-in-out; }
                .bounce-2 { animation: bounce 0.6s infinite 0.2s ease-in-out; }
                .bounce-3 { animation: bounce 0.6s infinite 0.4s ease-in-out; }
                @keyframes bounce {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-4px); }
                }
            `}</style>
            </Flex>
        </Flex>
    );
}
